import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import { UpsertMode } from 'components/patient-entry/PatientEntryView';
import QuestionSetView from 'components/patient-entry/QuestionSetView';
import { DataEntry, DataEntryValue, Label } from 'components/patient-entry/styled';
import { renderProblematicPatient } from 'components/patient-entry/upsert/PatientPane';
import UpsertAnamnesis from 'components/patient-entry/upsert/UpsertAnamnesis';
import moment from 'moment';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { DoctorDto, QuestionSetDto, TreatmentBookingDto, UpsertPatientDto } from 'ts-types/api.types';
import { IndexSignature } from 'utils/praxTypes';

const MainDiv = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

const ContentPane = styled.div`
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: min-content minmax(min-content, 1fr);
  column-gap: 3.5rem;
  overflow-y: auto;
`;

const DataEntryContainer = styled.div`
  flex: 1 1 auto;
  flex-direction: column;

  .date-time-value {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1rem;
  }

  .yesno-button.ui.mini.button {
    padding: 0.525rem;
    min-width: 3rem;
    margin-right: 0.5rem;
    font-size: 0.85rem;
  }

`

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr) minmax(3.5rem, min-content);
    row-gap: 0.35rem;
    column-gap: 1.2rem;

    .result-question {
      color: #575757;
    }

    .result-answer.no-result {
      color: #a5a5a5;
    }
  }

`;

interface Props extends MandateConsumerRenderProps, MessageSourceProps, AuthConsumerRenderProps {
  className?: string;
  patient?: UpsertPatientDto;
  patientId: number;
  treatmentBooking: TreatmentBookingDto;
  onSave: (treatmentDto?: Partial<TreatmentBookingDto>) => void;
  onCancel: () => void;
  setReferralDoctor: (doctor: DoctorDto) => void
  mode: UpsertMode;
}

interface State extends IndexSignature {
  componentDidMount: boolean;
  mandates: DropdownOption[];
  treatmentData?: Partial<TreatmentBookingDto>;
  treatmentValidationError?: boolean;
  currentQuestionSet?: QuestionSetDto;
  errorMessage?: string;
  cancelTokenSource: CancelTokenSource;
}

class PatientAnamnesis extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {currentMandates} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      componentDidMount: false,
      mandates: mapToDropdownOptionArray(currentMandates!, "name"),
      cancelTokenSource
    };
  }

  componentDidMount() {
    this.setState({componentDidMount: true});
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  updateAnamnesis = (treatmentBookingDto: Partial<TreatmentBookingDto>, hasValidationError: boolean) => {
    this.setState((prevState) => {
      return {
        treatmentData: {
          ...prevState.treatmentData,
          ...treatmentBookingDto
        },
        treatmentValidationError: hasValidationError
      }
    });
  }

  submitTreatmentBooking = async () => {
    const form = document.getElementById("treatment-booking-anamnesis-form");
    form!.dispatchEvent(new Event('submit', {cancelable: true}));
  }

  saveAnamnesis = async () => {
    const {getMessage, onSave} = this.props;
    const {treatmentData, treatmentValidationError} = this.state;

    if (!treatmentData || treatmentValidationError) {
      return;
    }

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      });
    };

    // await upsertAnamnesis(patientId, treatmentData, cancelTokenSource)
    //     .then((upsertAnamnesisDto) => {
    //       this.setState({
    //         treatmentData: upsertAnamnesisDto,
    //         treatmentValidationError: false
    //       });
    //       onSave(upsertAnamnesisDto)
    //     })
    //     .catch(handleError);

    this.setState({
      treatmentData: treatmentData,
      treatmentValidationError: false
    });

    onSave(treatmentData);
  }

  setQuestionSet = (questionSet?: QuestionSetDto) => {
    this.setState({
      currentQuestionSet: questionSet
    });
  }

  render(): React.ReactNode {

    const {patient, patientId, treatmentBooking, onCancel, mode, language, getMessage} = this.props;
    const {treatmentData, currentQuestionSet} = this.state;
    const submitting = false;

    let nameMap: { [lang: string]: string } = {};
    if (currentQuestionSet) {
      nameMap = {
        "de": currentQuestionSet.name,
        "en": currentQuestionSet.nameEn || currentQuestionSet.name,
        "fr": currentQuestionSet.nameFr || currentQuestionSet.name,
        "it": currentQuestionSet.nameIt || currentQuestionSet.name
      };
    }

    const className = this.props.className;
    return (
        <MainDiv className={className ? className : ""}>
          <ContentPane>
            <DataEntryContainer>
              {
                patient && (
                    <DataEntry>
                      <Label className="label">{getMessage("anamnesis.form.patient")}:</Label>
                      <DataEntryValue>
                        <div>
                          <span style={patient.doctorProblematic ? {color:"red"} : {}}>
                            {`${patient!.firstName} ${patient!.name} `}
                          </span>
                          {renderProblematicPatient(patient)}
                        </div>
                        <div>{`${moment(patient!.birthDate).format("DD.MM.YYYY")}`}</div>
                        <div>{`${patient!.street}`}</div>
                        <div>{`${patient!.zip} ${patient!.city}`}</div>
                      </DataEntryValue>
                    </DataEntry>
                )
              }
              <UpsertAnamnesis
                  patientId={patientId}
                  treatmentBooking={treatmentBooking}
                  mode={mode}
                  onTreatmentUpdate={this.updateAnamnesis}
                  onQuestionSetChange={this.setQuestionSet}
                  onSubmit={this.saveAnamnesis}
                  setReferralDoctor={this.props.setReferralDoctor}
              />
            </DataEntryContainer>

            {
              currentQuestionSet
              && <QuestionSetView
                  questionSetName={nameMap[language]}
                  questionAnswerPairs={
                    treatmentData && treatmentData.questionAnswerPairs
                        ? treatmentData.questionAnswerPairs
                        : []
                  }/>
            }
          </ContentPane>

          <FormButtonsRow>
            <Button type="button" compact disabled={submitting} onClick={onCancel}>
              {getMessage("patientCentral.patientEntry.actions.back")}
            </Button>

            <Button type="button"
                    compact
                    primary
                    floated="right"
                    onClick={() => this.submitTreatmentBooking()}
                    disabled={submitting}
            >
              {getMessage("patientCentral.patientEntry.actions.next")}
            </Button>
          </FormButtonsRow>
        </MainDiv>
    );
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

}

export default withMessages(withMandateContext(withAuthContext(PatientAnamnesis)));
